// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportDesignerDataSourceStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**s
 * Data Source View Model
 */

import { action, computed, observable } from 'mobx';
import uiState from 'logic/Izenda.UiState';
import fetcher from 'fetchers/Izenda.ReportDesignerDataFetcher';
import constants from 'logic/data/Izenda.ReportDesignerConstants';
import {
  every,
  find,
  isEmpty,
  flow,
  groupBy,
  map,
  head,
  isBoolean,
  some,
  filter,
  toString,
  uniqBy,
  isNil,
  contains
} from 'lodash/fp';
import { concatTree, parseToTree, updateTree } from 'share/utils/Izenda.LazyLoadingUtils';
import {
  loadRelationship,
  startLoadingRelationship as _startLoadingRelationship,
  endLoadingRelationship as _endLoadingRelationship,
  hasRemovedRelationship
} from 'logic/Izenda.ReportDesignerRelationshipStore';

class ReportDesignerDataSourceStore {
  @computed
  get model() {
    return this.reportStore.model.dataSource;
  }

  @computed
  get dataSourceDetail() {
    return this.model.dataSourceDetail;
  }

  @computed
  get dataTree() {
    return this.model.dataTree;
  }

  @computed
  get tenantId() {
    return uiState.getTenantId();
  }

  @computed
  get fieldsStoreDataTree() {
    return this.reportStore.fieldsStore.dataTree.details;
  }

  @observable isAllQuerySourceUncategorized = false;

  constructor(reportStore: any) {
    // master store
    this.reportStore = reportStore;
    // Update isAllQuerySourceUncategorized only one time
    this.stopUpdateIsAllQuerySource = false;
    // this.lazyLoadingPartialPromise = Promise.resolve();
  }

  /**
   * Load Initial Data
   */
  @action
  loadInitial(reportId: any) {
    // Update selected query source if exist
    this.updateSelectedQuerySource().then(this.updateRelationshipsQuerySourceCategory.bind(this));

    // Get report mode
    const currentReportId = reportId ? reportId : this.reportStore.reportId;
    return fetcher.getReportMode(currentReportId).then(this.model.readReportMode.bind(this.model));
  }

  /**
   * Load Initial Data in case directed from report viewer
   */
  @action
  loadInitialWithUpdateFilter(reportId: string): any {
    // Check and call needed api only
    if (isEmpty(this.fieldsStoreDataTree)) {
      this.getFilterData();
      this.loadQuerySourceFieldsForUpdateRelationship(this.reportStore.reportId);
    }
    return this.loadInitial(reportId);
  }

  @action
  updateSelectedQuerySource() {
    if (this.fieldsStoreDataTree.length) {
      let availableQuerySources = this.fieldsStoreDataTree[0].querySource;

      if (this.model.reportMode === constants.SIMPLE) {
        this.setSelectedTreeFromFieldTree(availableQuerySources);
        return Promise.resolve();
      }

      const relationshipIds = flow(filter({ dataSourceCategoryId: null }), map('id'))(availableQuerySources);

      // When switch from Fields to Data Source, get original query sources from join alias query sources
      return this.loadOriginalJoinAliasQuerySource(relationshipIds).then(originalQuerySources => {
        originalQuerySources &&
          originalQuerySources.forEach(originalQuerySource => {
            const joinAliasQuerySource = find({ id: originalQuerySource.relationshipId }, availableQuerySources);

            // Set correct data for selected tree
            if (joinAliasQuerySource) {
              joinAliasQuerySource.selected = true;
              joinAliasQuerySource.id = originalQuerySource.querySourceId;
              joinAliasQuerySource.dataSourceCategoryId = originalQuerySource.dataSourceCategoryId;
              joinAliasQuerySource.dataSourceCategoryName = originalQuerySource.dataSourceCategoryName;
              joinAliasQuerySource.name = originalQuerySource.querySourceName;
            }
          });

        // Avoid duplicate when self-join
        availableQuerySources = uniqBy('id', availableQuerySources);
        this.setSelectedTreeFromFieldTree(availableQuerySources);
      });
    }

    return Promise.resolve();
  }

  /**
   * Get data for filter when load data source in report designer
   */
  getFilterData(): void {
    const reportFilter = this.reportStore.model.reportFields.reportFilter;
    return fetcher.loadFilterOperatorGroup().then(querySource => {
      /* report filter */
      !isNil(querySource) && reportFilter.populateFilterQuerySource(querySource);
      reportFilter.configFieldList.forEach(configField => {
        if (
          contains(configField.filterInfo.sourceType, ['fieldSource', 'inTimePeriod']) ||
          contains(configField.filterInfo.operator.key, ['equals(checkbox)', 'notequal(checkbox)'])
        ) {
          this.getOperatorValueSource(configField);
        }
      });
    });
  }

  /**
   * Load available query source for update relationship on data source page.
   * @param  {string} reportId
   */
  loadQuerySourceFieldsForUpdateRelationship(reportId: string): void {
    if (!isNil(reportId) && !isEmpty(reportId)) {
      fetcher.availableQuerySourceFields(reportId, this.reportStore.isLoadDraft).then(result => {
        if (!isNil(result)) {
          result.isFromDataSource = true;
          this.dataTree.readDataSource(result);
          this.reportStore.fieldsStore.dataTree.details = result;
          // Update selected query source if exist
          this.updateSelectedQuerySource().then(this.updateRelationshipsQuerySourceCategory.bind(this));
        }
      });
    }
  }

  /**
   * Set selected tree
   * @param {array} availableQuerySources
   */
  setSelectedTreeFromFieldTree(availableQuerySources: Array<any>) {
    this.dataTree.selectedTree = flow(
      groupBy('dataSourceCategoryId'),
      map(value => {
        const firstItem = head(value);
        return {
          id: firstItem.dataSourceCategoryId,
          name: firstItem.dataSourceCategoryName,
          querySource: value
        };
      })
    )(availableQuerySources);
  }

  /**
   * Load original join alias query source
   * @param {array} relationshipIds
   */
  loadOriginalJoinAliasQuerySource(relationshipIds: Array<any>) {
    if (isEmpty(relationshipIds)) {
      return Promise.resolve(null);
    }
    const data = {
      reportKey: {
        key: this.reportStore.model.reportId
      },
      tenantId: uiState.getTenantId(),
      relationshipIds
    };
    return fetcher.loadJoinQuerySourceByRelationship(data);
  }

  /**
   * toggle Selected/Unselected item
   * @param {type} dataObject
   * @param {type} isSelected
   */
  @action
  updateSelected(dataObject: any, isSelected: boolean) {
    dataObject.selected = isSelected;
    // only remove relationships when user unchecking dataObject
    !isSelected && this.dataSourceDetail.removeUncheckRelationships(dataObject);
    return Promise.resolve(dataObject);
  }

  /**
   * Build page info
   * @return {Object}
   */
  @action
  buildPageInfo() {
    const pageInfo = this.dataSourceDetail.pageInfo;
    pageInfo.buildPagingRequest();
    return pageInfo;
  }

  /**
   * Check has item selected
   * @param {type} selectedQuerySource
   */
  @action
  checkRemoveSelectedRelation(dataObject: any) {
    // Remove parameter in filter section
    this.model.removeFilterParams(dataObject);

    return Promise.resolve({
      hasRemovedRelationship: hasRemovedRelationship(this.dataTree, this.dataSourceDetail.dataSourceGrid, dataObject),
      dataObject
    });
  }

  /**
   * Load Data Source Detail
   * @param {Object} dataObject
   * @param {Boolean} isSelectingDataObject: is selecting dataObject or not
   */
  loadDataSourceDetail(dataObject: any, isSelectingDataObject: any) {
    let selectedQuerySource = [];

    if (dataObject) {
      selectedQuerySource = this.dataTree.getDataTreeItems(dataObject);
    } else if (this.dataTree) {
      selectedQuerySource = this.dataTree.getDataTreeItems();
    }

    if (isEmpty(selectedQuerySource)) {
      return Promise.resolve();
    }

    return this.loadDataSource(selectedQuerySource, dataObject, isSelectingDataObject);
  }

  /**
   * Search Data Source
   */
  searchDataSource() {
    const selectedItems = this.dataTree.getDataTreeItems(null);
    this.loadDataSource(selectedItems);
  }

  /**
   * Load Data Source
   * @param {type} selectedQuerySource
   */
  @action
  loadDataSource(selectedQuerySource: any, changedDataObject: any, isSelectingDataObject: any) {
    const populateDataSource = action(dataSourceDetail => {
      return this.dataSourceDetail.populateData(
        dataSourceDetail,
        changedDataObject,
        this.model.reportMode,
        isSelectingDataObject
      );
    });

    return loadRelationship(
      this.reportStore.model.reportId,
      this.model.reportMode,
      selectedQuerySource,
      this.buildPageInfo(),
      isBoolean(isSelectingDataObject),
      this.model.reportMode === constants.ADVANCED ? this.dataSourceDetail.getDbRelationshipIds() : []
    )
      .bind(this)
      .then(populateDataSource);
  }

  /**
   * Update category of query source in relationship, when category had been changed in data model
   */
  @action
  updateRelationshipsQuerySourceCategory() {
    if (!isEmpty(this.dataTree.selectedTree) && !isEmpty(this.dataSourceDetail.dataSourceGrid)) {
      const selectedQuerySources = this.dataTree.getSelectedQuerySources();

      selectedQuerySources.forEach(querySource => {
        const { id: querySourceId, dataSourceCategoryId, dataSourceCategoryName } = querySource;
        this.dataSourceDetail.dataSourceGrid.forEach(relationship => {
          // Update data source category of join query source
          if (
            relationship.joinQuerySourceId === querySourceId &&
            relationship.joinDataSourceCategoryId !== dataSourceCategoryId
          ) {
            relationship.joinDataSourceCategoryId = dataSourceCategoryId;
            relationship.joinDataSourceCategoryName = dataSourceCategoryName;
          }
          // Update data source category of foreign query source
          if (
            relationship.foreignQuerySourceId === querySourceId &&
            relationship.foreignDataSourceCategoryId !== dataSourceCategoryId
          ) {
            relationship.foreignDataSourceCategoryId = dataSourceCategoryId;
            relationship.foreignDataSourceCategoryName = dataSourceCategoryName;
          }
        });
      });
    }
  }

  /**
   * Go to page
   * @param {type} pageIndex
   */
  @action
  goToPage(pageIndex: any) {
    this.dataSourceDetail.pageInfo.setPageIndex(pageIndex);

    this.loadDataSourceDetail();
  }

  /**
   * Start Loading Relationship
   */
  startLoadingRelationship() {
    return _startLoadingRelationship(this.dataSourceDetail);
  }

  /**
   * End Loading Relationship
   */
  endLoadingRelationship() {
    return _endLoadingRelationship(this.dataSourceDetail);
  }

  /**
   * LazyLoading QuerySource
   * @param  {Object}  pageInfo
   * @param  {Boolean} isReset
   * @return {Promise}
   */
  @action
  lazyLoadingQuerySource(pageInfo: any, isReset: any, searchKey: any) {
    searchKey = searchKey || this.dataTree.filterText;
    const querySources = this.dataTree.getDataTreeItems();

    if (this.model.reportMode === constants.SIMPLE && some('selected', querySources)) {
      return this.lazyLoadingQuerySourceSimpleMode(searchKey);
    }

    return this.lazyLoadingDataSourceCategory(pageInfo, isReset, searchKey);
  }

  /**
   * LazyLoading QuerySource Simple Mode
   * @return {Promise}
   */
  @action
  lazyLoadingQuerySourceSimpleMode(searchKey) {
    if (this.model.reportMode === constants.ADVANCED) {
      return Promise.resolve();
    }

    // Cancel pending load partial promise when load related query source
    // if (this.lazyLoadingPartialPromise.isPending()) {
    //   this.lazyLoadingPartialPromise.cancel();
    // }

    const reportId = this.reportStore.model.reportId;
    const querySources = this.dataTree.getDataTreeItems();

    if (!some('selected', querySources)) {
      return this.lazyLoadingDataSourceCategory(null, true, searchKey);
    }

    const param = {
      objectId: reportId,
      querySources: querySources,
      tenantId: uiState.getTenantId(),
      criteria: [
        {
          key: 'Name',
          value: searchKey
        }
      ]
    };

    return fetcher.loadRelatedQuerySources(param).then(({ data, isLastPage, numOfChilds }) => {
      return this.processTreeData(data, isLastPage, numOfChilds, true);
    });
  }

  /**
   * LazyLoading DataSource Category
   * @param  {Object}  pageInfo
   * @param  {Boolean} isReset
   * @return {Promise}
   */
  @action
  lazyLoadingDataSourceCategory(pageInfo: any, isReset: any, searchKey: any) {
    pageInfo = pageInfo || this.lazyLoadingStore.pageInfoForFirstTime;

    const param = {
      tenantId: uiState.getTenantId(),
      reportKey: {
        key: this.reportStore.model.reportId
      },
      criteria: [
        {
          key: 'Name',
          value: searchKey
        }
      ],
      skipItems: pageInfo.fromIndex,
      pageSize: pageInfo.pageSize,
      parentIds: pageInfo.parentIds
    };

    var lazyLoadingPartialPromise = fetcher.loadPartialDataSourceCategory(param);
    return lazyLoadingPartialPromise.then(({ data, isLastPage, numOfChilds }) => {
      if (toString(searchKey) === toString(this.dataTree.filterText)) {
        return this.processTreeData(data, isLastPage, numOfChilds, isReset);
      } else {
        return Promise.resolve();
      }
    });
  }

  /**
   * Process TreeData
   * @param  {Object}  data
   * @param  {Boolean} isLastPage
   * @param  {Number}  numOfChilds
   * @param  {Boolean} isReset
   * @return {Object}
   */
  @action
  processTreeData(data: any, isLastPage: any, numOfChilds: any, isReset: any) {
    const dataTree = this.dataTree;
    const newDetails = dataTree.convertDataSource(data);
    dataTree.isLastPage = isLastPage;

    if (isReset) {
      dataTree.details = [];
      dataTree.displayData = [];
    }

    concatTree(dataTree.details, newDetails, constants.DATA_SOURCE_CONCAT_TREE_MAP, numOfChilds, []);

    updateTree(dataTree.details, dataTree.selectedTree, constants.DATA_SOURCE_CONCAT_TREE_MAP, ['selected']);

    dataTree.displayData = parseToTree(
      dataTree.details,
      dataTree.filterText ? constants.DATA_SOURCE_TREE_MAP_FOR_SEARCH : constants.DATA_SOURCE_TREE_MAP
    );

    if (!this.stopUpdateIsAllQuerySource) {
      this.stopUpdateIsAllQuerySource = true;
      this.isAllQuerySourceUncategorized = every(cat => cat.id === constants.EMPTY_GUID, dataTree.details);
    }

    return data;
  }

  /**
   * Register LazyLoading Store
   * @param  {Object} store
   */
  registerLazyLoadingStore(store: any) {
    this.lazyLoadingStore = store;
  }

  /**
   * Update Dynamic QuerySource Fields
   * @return {Promise}
   */
  updateDynamicQuerySourceFields() {
    const param = {
      tenantId: uiState.getTenantId(),
      reportKey: {
        key: this.reportStore.model.reportId
      }
    };

    return fetcher.loadDynamicDataSourceCategory(param).then(({ data, isLastPage, numOfChilds }) => {
      // Update field in selected tree
      this.updateDynamicQuerySourceInSelectedTree(data);
      return this.processTreeData(data, isLastPage, numOfChilds);
    });
  }

  /**
   * UpdateDynamicQuerySourceInSelectedTree
   * @param  {Object} data
   */
  @action
  updateDynamicQuerySourceInSelectedTree(data: any) {
    const { selectedTree } = this.dataTree;

    data.forEach(resultCategory => {
      const selectedCategory = find({ id: resultCategory.id }, selectedTree);
      if (selectedCategory) {
        resultCategory.querySource.forEach(resultQuerySource => {
          const selectedQuerySource = find({ id: resultQuerySource.id }, selectedCategory.querySource);
          selectedQuerySource && (selectedQuerySource.fields = resultQuerySource.fields);
        });
      }
    });
  }

  /**
   * Update selected tree
   * @param  {Object}  dataSource
   * @param  {Boolean}  status
   * @param  {Boolean} isSearching
   * @return {Promise}
   */
  @action
  updateSelectedTree(dataSource: any, status: boolean, isSearching: boolean) {
    const { selectedTree } = this.dataTree;
    const { dataSourceCategoryId, dataSourceCategoryName } = dataSource;
    const { model: { reportMode }, reportStore: { model: { reportId } } } = this;

    const existedCategoryNode = find({ id: dataSourceCategoryId }, selectedTree);
    const querySourceClone = Object.assign({}, dataSource, {
      selected: status,
      aliasId: dataSource.aliasId,
      isStoreProcedure: dataSource.isStoreProcedure
    });

    let didAddNewQuerySource = false;

    if (existedCategoryNode) {
      const existedQueryNode = find({ id: dataSource.id }, existedCategoryNode.querySource);
      // Should not delete query source from this tree as it will make relationship go wrong
      // Mark unselect status to tell loadRelationships API that we unselected those fields
      // loadRelationships API cannot change since it was published
      if (existedQueryNode) {
        existedQueryNode.selected = status;
      } else {
        existedCategoryNode.querySource.push(querySourceClone);
        didAddNewQuerySource = true;
      }
    } else {
      const newCategoryNode = {
        id: dataSourceCategoryId,
        name: dataSourceCategoryName,
        querySource: [querySourceClone]
      };

      selectedTree.push(newCategoryNode);
      didAddNewQuerySource = true;
    }

    // When new query source is add to the tree, load its fields
    // Only load when user is searching and advanced mode (for relationship), otherwise use data source fields
    if (didAddNewQuerySource && isSearching && reportMode === constants.ADVANCED) {
      const param = {
        reportKey: {
          key: reportId
        },
        tenantId: uiState.getTenantId(),
        querySourceId: querySourceClone.id
      };
      return fetcher.loadDataSourceFields(param).then(fields => {
        querySourceClone.fields = fields;
      });
    }

    return Promise.resolve();
  }

  /**
   * Handle change selected querySource
   * @param  {Object} dataObject
   * @param  {Boolean} state
   * @param  {TreeNode} node
   * @return {Promise}
   */
  @action
  handleChangeSelectedQuerySource(dataObject: any, state: boolean, node: any) {
    this.updateSelected(dataObject, state);

    if (!state && dataObject.isDynamic && !every('isParameter', dataObject.fields)) {
      dataObject.fields = filter('isParameter', dataObject.fields);
      node.childNodes = filter(node => node.source.isParameter, node.childNodes);
    }

    return Promise.all([
      this.updateSelectedTree(dataObject, state, !isEmpty(this.dataTree.filterText)),
      this.loadDataSourceDetail(dataObject, state),
      this.lazyLoadingQuerySourceSimpleMode(this.dataTree.filterText)
    ]);
  }

  /**
   * Reset data source grid dirty
   */
  @action
  resetDataSourceGridDirty() {
    this.dataSourceDetail.resetDataSourceGridDirty();
  }
}

export default ReportDesignerDataSourceStore;



// WEBPACK FOOTER //
// ./ui/logic/Izenda.ReportDesignerDataSourceStore.js